import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { submitRsvp } from "@/lib/api";
import { toast } from "sonner";
import { Phone, User, CheckCircle2 } from "lucide-react";

export const RsvpDialog = ({ event, open, onOpenChange }) => {
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setMobile("");
      setDone(false);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!event) return;
    if (!name.trim()) return toast.error("Please enter your name");
    const clean = mobile.replace(/[\s-]/g, "");
    if (!/^\+?\d{7,15}$/.test(clean)) return toast.error("Please enter a valid mobile number");
    setLoading(true);
    try {
      await submitRsvp(event.id, name.trim(), clean);
      setDone(true);
      toast.success("RSVP confirmed! See you there.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit RSVP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="rsvp-dialog"
        className="rounded-2xl border border-zinc-800 bg-zinc-950 text-white p-0 max-w-md"
      >
        <div className="p-6 md:p-8">
          {!done ? (
            <>
              <DialogHeader className="text-left mb-6">
                <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[#FF5612] mb-2">
                  Reserve your seat
                </span>
                <DialogTitle className="font-display font-extrabold text-2xl tracking-tight leading-tight text-white">
                  {event?.title}
                </DialogTitle>
                <DialogDescription className="text-sm text-zinc-400 mt-1">
                  {event?.store_name} · {event?.store_city}
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="rsvp-form">
                <div>
                  <label className="text-[10px] font-semibold tracking-[0.18em] uppercase text-zinc-500 mb-2 block">
                    Full Name
                  </label>
                  <div className="flex items-center gap-3 bg-zinc-900 rounded-xl border border-zinc-800 focus-within:border-[#FF5612] px-4 transition-colors">
                    <User className="w-4 h-4 text-zinc-500" />
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="John Doe"
                      data-testid="rsvp-name-input"
                      className="w-full bg-transparent py-3 outline-none placeholder:text-zinc-600 text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-semibold tracking-[0.18em] uppercase text-zinc-500 mb-2 block">
                    Mobile Number
                  </label>
                  <div className="flex items-center gap-3 bg-zinc-900 rounded-xl border border-zinc-800 focus-within:border-[#FF5612] px-4 transition-colors">
                    <Phone className="w-4 h-4 text-zinc-500" />
                    <input
                      type="tel"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value)}
                      placeholder="+91 98XXX XXXXX"
                      data-testid="rsvp-mobile-input"
                      className="w-full bg-transparent py-3 outline-none placeholder:text-zinc-600 text-white"
                    />
                  </div>
                  <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
                    We'll send event reminders & directions to this number.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="rsvp-submit-btn"
                  className="w-full bg-[#FF5612] text-white py-3.5 px-6 rounded-xl font-bold text-sm hover:bg-[#FF6B2B] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? "Confirming..." : "Confirm RSVP"}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="rsvp-success" className="text-center py-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-zinc-900 border border-[#FF5612] flex items-center justify-center mb-5 zl-pulse-orange">
                <CheckCircle2 className="w-8 h-8 text-[#FF5612]" />
              </div>
              <h3 className="font-display font-extrabold text-2xl tracking-tight mb-2 text-white">
                You're In!
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed mb-6">
                Your RSVP for <span className="font-semibold text-white">{event?.title}</span> at{" "}
                <span className="font-semibold text-white">{event?.store_name}</span> is confirmed.
              </p>
              <button
                onClick={() => onOpenChange(false)}
                data-testid="rsvp-close-btn"
                className="w-full rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white py-3 px-6 font-bold text-sm transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RsvpDialog;
